import React, { useState } from 'react';

import adminApi, { pagingParams } from '../../Api/adminApi';
import PaginatedTable from '../../MyComponents/PaginatedTable/PaginatedTable';
import useAdminQuery from './useAdminQuery';
import StateCard from './StateCard';
import UsersFilters from './UsersFilters';
import { usersColumns } from './usersColumns';

export const DEFAULT_USER_FILTERS = {
    search: '',
    plan: '',
    status: '',
    lastLoginDays: '',
    excludeGuests: true,
};

/**
 * Every account with its plan, status and activity counters. Clicking a row
 * opens the user drawer (owned by AdminPage, keyed by ?user=).
 *
 * Also used by the Subscriptions tab with `columns`, `fixedFilters` and
 * `hidePlanFilter` to show one plan's subscribers.
 */
const UsersTab = ({
    onOpenUser,
    selectedUserId,
    refreshKey,
    columns = usersColumns,
    fixedFilters = {},
    hidePlanFilter = false,
    initialSort = { orderBy: 'lastLogin', order: 'desc' },
    title = 'Users',
}) => {
    const [filters, setFiltersState] = useState(DEFAULT_USER_FILTERS);
    const [order, setOrder] = useState(initialSort.order);
    const [orderBy, setOrderBy] = useState(initialSort.orderBy);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const effective = { ...filters, ...fixedFilters };
    const query = {
        ...pagingParams({ rowsPerPage, page, orderBy, order }),
        search: effective.search,
        plan: effective.plan,
        status: effective.status,
        lastLoginDays: effective.lastLoginDays,
        excludeGuests: effective.excludeGuests === false ? 'false' : 'true',
    };
    const queryKey = JSON.stringify(query);

    const { data, isLoading, isError, isForbidden } = useAdminQuery(() => adminApi.getUsers(query), [
        queryKey,
        refreshKey,
    ]);

    const setFilters = (next) => {
        setFiltersState(next);
        setPage(0);
    };

    const setOrderFlags = (nextOrder, nextOrderBy) => {
        setOrder(nextOrder);
        setOrderBy(nextOrderBy);
        setPage(0);
    };

    if (isForbidden || (isError && !data)) {
        return <StateCard isError={isError} isForbidden={isForbidden} />;
    }

    const rows = data?.users || [];
    const count = data?.count || 0;

    return (
        <>
            <UsersFilters filters={effective} onChange={setFilters} hidePlan={hidePlanFilter} />
            <PaginatedTable
                title={`${title} (${count})`}
                headCells={columns}
                rows={rows}
                rowCount={count}
                order={order}
                orderBy={orderBy}
                setOrder={setOrderFlags}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                isLoading={isLoading}
                onRowClick={(row) => onOpenUser(row.id)}
                selectedId={selectedUserId}
                emptyMessage="No users match these filters."
            />
        </>
    );
};

export default UsersTab;
